import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
// material-ui components
import { Grid, Checkbox } from '@mui/material';
import { TextField, IconButton, Autocomplete, Card } from '@totalsoft/rocket-ui';
import PersonIcon from '@mui/icons-material/Person';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import debounce from 'lodash.debounce';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import CancelIcon from '@mui/icons-material/Cancel';
import { withRights } from 'providers/RightsConsumer';
import PhoneNumberField from 'components/common/inputs/PhoneNumberTextField';

export const OfferPartnerContactPersonsDataRowComponent = ({
  contactPerson,
  deleteContactPerson,
  onPropertyChange,
  openMasterPartner,
  openDocumentPartner,
  masterPartnerApp,
  positions,
  loadPersons,
  validation,
  t,
  loadPartnerDetails,
  idx,
  clientContactinfoWrite,
  rights,
  onContactPersonClear,
  showESignatureCheckboxFromContactPersonsCard
}) => {
  const onAutocompleteChange = onPropertyChange => suggestion => {
    if (suggestion) onPropertyChange(suggestion.id ? suggestion.id : -1, suggestion.uniqueID);
  };
  const disabled = rights.readOnlyFull || !clientContactinfoWrite;
  return (
    <Card>
      <Grid container spacing={2} key={contactPerson.contactInfo} alignItems='center'>
        <Grid item xs={12} md={3} sm={6}>
          <div id={'offerPartnerContactPersonsCard_' + idx + '_uniqueID'}>
            <Autocomplete
              fullWidth
              creatable={true}
              value={contactPerson || ''}
              valueKey='id'
              labelKey='uniqueID'
              label={t('OfferEdit.Client.ContactPersons.PersonUniqueID')}
              onChange={onAutocompleteChange(onPropertyChange('personId', 'uniqueID', loadPartnerDetails))}
              loadOptions={debounce(loadPersons, 500, { leading: true })}
              disabled={disabled}
              onClearValue={onContactPersonClear}
            />
          </div>
        </Grid>
        <Grid item xs={12} md={3} sm={6}>
          <TextField
            id={'offerPartnerContactPersonsCard_' + idx + '_personName'}
            name={'personName'}
            fullWidth
            required
            value={contactPerson.personName || ''}
            onChange={value => onPropertyChange('personName')(value)}
            label={t('OfferEdit.Client.ContactPersons.PersonName')}
            error={!isValid(validation.personName)}
            helperText={getErrors(validation.personName)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={3} sm={6}>
          <TextField
            id={'offerPartnerContactPersonsCard_' + idx + '_email'}
            name={'email'}
            fullWidth
            value={contactPerson.email || ''}
            label={t('OfferEdit.Client.ContactPersons.Email')}
            onChange={value => onPropertyChange('email')(value)}
            error={!isValid(validation.email)}
            helperText={getErrors(validation.email)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={3} sm={6}>
          <PhoneNumberField
            id={'offerPartnerContactPersonsCard_' + idx + '_phone'}
            country={contactPerson.phoneCountryCode}
            name={'phone'}
            fullWidth
            size='small'
            label={t('OfferEdit.Client.ContactPersons.MobilePhone')}
            value={contactPerson.phone || ''}
            onChange={value => onPropertyChange('phone')(value)}
            error={!isValid(validation.phone)}
            helperText={getErrors(validation.phone)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={3} sm={6}>
          <Autocomplete
            id={'offerPartnerContactPersonsCard_' + idx + '_position'}
            fullWidth
            required
            value={contactPerson.positionId || ''}
            labelKey='positionName'
            valueKey='positionId'
            simpleValue
            label={t('OfferEdit.Client.ContactPersons.Position')}
            onChange={value => onPropertyChange('positionId')(value)}
            options={positions}
            error={!isValid(validation.positionId)}
            helperText={getErrors(validation.positionId)}
            disabled={disabled}
          />
        </Grid>

        <Grid item xs={12} md={showESignatureCheckboxFromContactPersonsCard ? 2 : 3} sm={6}>
          <span>{t('OfferEdit.Client.ContactPersons.Default')}</span>
          <Checkbox
            id={'offerPartnerContactPersonsCard_' + idx + '_isDefault'}
            name={'isDefaultContact'}
            checked={(contactPerson && contactPerson.isDefaultContact) || false}
            onChange={onCheckBoxChange(onPropertyChange('isDefaultContact'))}
            color='primary'
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={3} sm={6}>
          <span>{t('OfferEdit.Client.ContactPersons.IsLegalRepresentative')}</span>
          <Checkbox
            id={'offerPartnerContactPersonsCard_' + idx + '_isLegalRepresentative'}
            name={'isLegalRepresentative'}
            checked={contactPerson?.isLegalRepresentative || false}
            onChange={onCheckBoxChange(onPropertyChange('isLegalRepresentative'))}
            color='primary'
            disabled={disabled}
          />
        </Grid>
        {showESignatureCheckboxFromContactPersonsCard && (
          <Grid item xs={12} md={2} sm={6}>
            <span>{t('OfferEdit.Client.ContactPersons.ESignature')}</span>
            <Checkbox
              id={'offerPartnerContactPersonsCard_' + idx + '_isESignature'}
              name={'isESignature'}
              checked={(contactPerson && contactPerson.isESignature) || false}
              onChange={onCheckBoxChange(onPropertyChange('isESignature'))}
              color='primary'
              disabled={disabled}
            />
          </Grid>
        )}

        <Grid item container xs={12} md={showESignatureCheckboxFromContactPersonsCard ? 2 : 3} sm={6} justifyContent='flex-end'>
          {(!!contactPerson.personId || !!contactPerson.documentPartnerId) && contactPerson.positionId && masterPartnerApp && (
            <Grid item>
              <IconButton
                color='primary'
                variant='text'
                size='small'
                tooltip={t('Tooltips.EditPartner')}
                onClick={
                  !contactPerson.documentPartnerId
                    ? openMasterPartner(contactPerson.personId)
                    : openDocumentPartner(contactPerson.documentPartnerId, contactPerson.personName, 'Contact')
                }
              >
                <PersonIcon />
              </IconButton>
            </Grid>
          )}
          <Grid item>
            <IconButton
              id={'offerPartnerContactPersonsCard_deleteBtn'}
              color='primary'
              variant='text'
              size='small'
              tooltip={t('Tooltips.RemovePartner')}
              onClick={deleteContactPerson}
              disabled={disabled}
            >
              <CancelIcon />
            </IconButton>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id={'offerPartnerContactPersonsCard_' + idx + '_comments'}
            label={t('OfferEdit.Client.Comment')}
            multiline
            inputProps={{ maxLength: 1000 }}
            rows={3}
            value={contactPerson.comments || ''}
            onChange={value => onPropertyChange('comments')(value)}
            variant='outlined'
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </Card>
  );
};

OfferPartnerContactPersonsDataRowComponent.propTypes = {
  deleteContactPerson: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  openMasterPartner: PropTypes.func.isRequired,
  openDocumentPartner: PropTypes.func.isRequired,
  masterPartnerApp: PropTypes.bool,
  contactPerson: PropTypes.object.isRequired,
  positions: PropTypes.array.isRequired,
  loadPersons: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  loadPartnerDetails: PropTypes.func,
  idx: PropTypes.number.isRequired,
  clientContactinfoWrite: PropTypes.bool.isRequired,
  rights: PropTypes.object.isRequired,
  onContactPersonClear: PropTypes.func.isRequired,
  showESignatureCheckboxFromContactPersonsCard: PropTypes.bool.isRequired,
  isAssociatedPartner: PropTypes.bool.isRequired
};
export default withTranslation()(withRights(OfferPartnerContactPersonsDataRowComponent));
